/**
 * Golf Club Brands Database
 * Top 20 brands ranked by market presence, innovation, and tour usage (2025)
 * Includes legacy models from 2010-2024 for comprehensive coverage
 */

export interface GolfBrand {
  id: string;
  name: string;
  rank: number;
  tier: 'premium' | 'mid' | 'value' | 'dtc';
  specialties: string[];
  popularModels?: {
    drivers?: string[];
    woods?: string[];
    hybrids?: string[];
    irons?: string[];
    wedges?: string[];
    putters?: string[];
  };
}

export const GOLF_BRANDS: GolfBrand[] = [
  {
    id: 'taylormade',
    name: 'TaylorMade',
    rank: 1,
    tier: 'premium',
    specialties: ['Drivers', 'Speed Tech', 'Tour Preferred'],
    popularModels: {
      // Current + Legacy (2010-2024)
      drivers: ['Qi35', 'Qi10', 'Stealth 2', 'Stealth', 'SIM2', 'SIM', 'M6', 'M5', 'M4', 'M3', 'M2', 'M1', 'SLDR', 'R15', 'R11', 'R9', 'Burner SuperFast'],
      woods: ['Qi35 Fairway', 'Stealth 2 Fairway', 'SIM2 Fairway', 'SIM Fairway', 'M6 Fairway', 'M4 Fairway', 'M2 Fairway', 'RocketBallz'],
      hybrids: ['Qi35 Rescue', 'Stealth 2 Rescue', 'SIM Rescue', 'M6 Rescue', 'M4 Rescue', 'M2 Rescue', 'RocketBallz Rescue'],
      irons: ['P790', 'P770', 'P7MC', 'P7MB', 'Stealth', 'SIM2 Max', 'SIM Max', 'M6', 'M4', 'M2', 'RSi 2', 'RSi 1', 'SpeedBlade', 'RocketBladez', 'Burner 2.0'],
      wedges: ['Milled Grind 4', 'MG3', 'MG2', 'Milled Grind', 'Hi-Toe 3', 'Hi-Toe', 'EF Spin', 'ATV'],
      putters: ['Spider GT', 'Spider GTx', 'Spider X', 'Spider Tour', 'TP Hydro Blast', 'Ghost Spider']
    }
  },
  {
    id: 'callaway',
    name: 'Callaway',
    rank: 2,
    tier: 'premium',
    specialties: ['AI Design', 'Forgiveness', 'Distance'],
    popularModels: {
      // Current + Legacy (2010-2024)
      drivers: ['Paradym Ai Smoke', 'Paradym', 'Rogue ST', 'Rogue', 'Epic Flash', 'Epic', 'Mavrik', 'XR 16', 'XR', 'Big Bertha Fusion', 'Big Bertha Alpha', 'Big Bertha', 'RAZR Fit', 'Diablo Octane'],
      woods: ['Paradym Fairway', 'Rogue ST Fairway', 'Mavrik Fairway', 'Epic Flash Fairway', 'Epic Fairway', 'XR Fairway', 'Big Bertha Fairway'],
      hybrids: ['Paradym Hybrid', 'Apex Hybrid', 'Rogue ST Hybrid', 'Mavrik Hybrid', 'Epic Hybrid', 'XR Hybrid', 'Big Bertha Hybrid', 'X Hot Hybrid'],
      irons: ['Paradym Ai Smoke', 'Apex', 'Apex Pro', 'Rogue ST Max', 'Mavrik', 'Mavrik Max', 'Epic Forged', 'XR', 'XR Pro', 'X Hot', 'X-22', 'X-20', 'X-18', 'X-14', 'Apex CF 16', 'Big Bertha'],
      wedges: ['JAWS Raw', 'JAWS Full Toe', 'JAWS MD5', 'MD5', 'Mack Daddy 5', 'Mack Daddy 4', 'Mack Daddy 3', 'Mack Daddy 2', 'Mack Daddy CB', 'Mack Daddy Forged'],
      putters: ['Odyssey Ai-One', 'White Hot OG', 'Stroke Lab', 'Triple Track']
    }
  },
  {
    id: 'titleist',
    name: 'Titleist',
    rank: 3,
    tier: 'premium',
    specialties: ['Precision', 'Tour Play', 'Feel'],
    popularModels: {
      // Current + Legacy (2010-2024)
      drivers: ['TSR4', 'TSR3', 'TSR2', 'TSR1', 'TSi4', 'TSi3', 'TSi2', 'TSi1', 'TS4', 'TS3', 'TS2', 'TS1', '917 D3', '917 D2', '915 D3', '915 D2', '913 D3', '913 D2', '910 D3', '910 D2', '909 D3'],
      woods: ['TSR3 Fairway', 'TSR2 Fairway', 'TSi Fairway', 'TS Fairway', '917 Fairway', '915 Fairway', '913 Fairway', '910 Fairway'],
      hybrids: ['TSR3 Hybrid', 'TSR2 Hybrid', 'TSi Hybrid', 'TS Hybrid', '818 Hybrid', '816 Hybrid', '915 Hybrid', '913 Hybrid', '910 Hybrid'],
      irons: ['T100', 'T100S', 'T150', 'T200', 'T300', 'T350', 'T400', 'AP1', 'AP2', 'AP3', 'CB', 'MB', '718 AP1', '718 AP2', '718 AP3', '716 AP1', '716 AP2', '714 AP1', '714 AP2', '714 CB', '714 MB', '712 AP1', '712 AP2', '712 CB', '712 MB', '710 AP1', '710 AP2'],
      wedges: ['Vokey SM10', 'Vokey SM9', 'Vokey SM8', 'Vokey SM7', 'Vokey SM6', 'Vokey SM5', 'Vokey SM4', 'Vokey TVD', 'BV Wedge'],
      putters: ['Scotty Cameron Special Select', 'Scotty Cameron Studio Select', 'Scotty Cameron Phantom', 'Scotty Cameron Futura', 'Scotty Cameron GoLo', 'Scotty Cameron Newport']
    }
  },
  {
    id: 'ping',
    name: 'Ping',
    rank: 4,
    tier: 'premium',
    specialties: ['Fitting', 'Game Improvement', 'Stability'],
    popularModels: {
      // Current + Legacy (2010-2024)
      drivers: ['G430', 'G430 Max', 'G430 LST', 'G425', 'G425 Max', 'G425 LST', 'G410', 'G410 Plus', 'G410 LST', 'G400', 'G400 Max', 'G400 LST', 'G30', 'G25', 'G20', 'G15', 'G', 'i25', 'i20', 'i15', 'Anser'],
      woods: ['G430 Fairway', 'G425 Fairway', 'G410 Fairway', 'G400 Fairway', 'G30 Fairway', 'G25 Fairway', 'G20 Fairway', 'G15 Fairway'],
      hybrids: ['G430 Hybrid', 'G425 Hybrid', 'G410 Hybrid', 'G400 Hybrid', 'G30 Hybrid', 'G25 Hybrid', 'G20 Hybrid', 'G15 Hybrid', 'G Hybrid'],
      irons: ['G430', 'i530', 'i525', 'i500', 'i210', 'i200', 'Blueprint', 'Blueprint S', 'Blueprint T', 'G425', 'G410', 'G400', 'G30', 'G25', 'G20', 'G15', 'G', 'i25', 'i20', 'i15', 'i10', 'i5', 'i3', 'Eye2', 'Eye2 XG', 'S55', 'S56', 'S57', 'S58', 'S59', 'Anser'],
      wedges: ['Glide 4.0', 'Glide Forged Pro', 'Glide 3.0', 'Glide 2.0', 'Glide', 'Gorge'],
      putters: ['Anser', 'DS72', 'Tyne', 'Fetch', 'Harwood', 'Kushin', 'PLD', 'Sigma 2']
    }
  },
  {
    id: 'cobra',
    name: 'Cobra',
    rank: 5,
    tier: 'mid',
    specialties: ['Affordability', 'Forgiveness', 'Bold Design'],
    popularModels: {
      // Current + Legacy (2010-2024)
      drivers: ['DS-ADAPT', 'Darkspeed', 'Darkspeed Max', 'Aerojet', 'Aerojet Max', 'LTDx', 'Radspeed', 'SpeedZone', 'SpeedZone Xtreme', 'F9', 'F8', 'F8+', 'F7', 'F7+', 'Fly-Z', 'Fly-Z+', 'Bio Cell', 'Bio Cell+', 'AMP Cell'],
      woods: ['Darkspeed Fairway', 'Aerojet Fairway', 'LTDx Fairway', 'Radspeed Fairway', 'SpeedZone Fairway', 'F9 Fairway', 'F8 Fairway', 'F7 Fairway', 'Fly-Z Fairway'],
      hybrids: ['DS-ADAPT Hybrid', 'Darkspeed Hybrid', 'Aerojet Hybrid', 'LTDx Hybrid', 'Radspeed Hybrid', 'SpeedZone Hybrid', 'F9 Hybrid', 'F8 Hybrid', 'F7 Hybrid', 'Fly-Z Hybrid'],
      irons: ['Darkspeed', 'Darkspeed One Length', 'Aerojet', 'King Tour', 'King Tour MIM', 'LTDx', 'Radspeed', 'SpeedZone', 'F9', 'F8', 'F7', 'Fly-Z XL', 'Fly-Z', 'Bio Cell', 'AMP Cell', 'Forged Tec', 'Forged Tec One Length'],
      wedges: ['King Snakebite', 'King MIM', 'PUR', 'Trusty Rusty'],
      putters: ['3D Printed King', 'King Vintage', 'Supernova']
    }
  },
  {
    id: 'mizuno',
    name: 'Mizuno',
    rank: 6,
    tier: 'premium',
    specialties: ['Forged Feel', 'Craftsmanship', 'Irons'],
    popularModels: {
      // Current + Legacy (2010-2024)
      drivers: ['ST-Max 230', 'ST-Z 230', 'ST-X 230', 'ST-Z', 'ST-X', 'ST200', 'ST190', 'GT180', 'JPX 900', 'JPX 850', 'JPX EZ'],
      woods: ['ST-Max Fairway', 'ST-Z Fairway', 'ST-X Fairway', 'ST200 Fairway', 'ST190 Fairway', 'GT180 Fairway', 'JPX 900 Fairway', 'JPX 850 Fairway'],
      hybrids: ['Fli-Hi', 'CLK Hybrid', 'ST-Z Hybrid', 'ST-X Hybrid', 'ST200 Hybrid', 'ST190 Hybrid', 'JPX 900 Hybrid', 'JPX 850 Hybrid', 'JPX Fli-Hi'],
      irons: ['JPX 925', 'JPX 925 Tour', 'JPX 925 Forged', 'JPX 923', 'JPX 923 Tour', 'JPX 923 Forged', 'JPX 923 Hot Metal', 'JPX 921', 'JPX 921 Tour', 'JPX 921 Forged', 'JPX 921 Hot Metal', 'JPX 919', 'JPX 919 Tour', 'JPX 919 Forged', 'JPX 919 Hot Metal', 'JPX 900', 'JPX 900 Tour', 'JPX 900 Forged', 'JPX 900 Hot Metal', 'JPX 850', 'JPX 850 Forged', 'JPX 825', 'JPX 825 Pro', 'JPX 800', 'JPX 800 Pro', 'Pro 225', 'Pro 223', 'Pro 221', 'MP-25', 'MP-20', 'MP-18', 'MP-69', 'MP-68', 'MP-64', 'MP-63', 'MP-62', 'MP-59', 'MP-58', 'MP-57', 'MP-52', 'MP-32', 'MX-300', 'MX-200'],
      wedges: ['T24', 'T22', 'T20', 'ES21', 'S18', 'S5', 'MP-T Series'],
      putters: ['M.Craft', 'M.Craft Type I', 'M.Craft Type II', 'Bettinardi']
    }
  },
  {
    id: 'srixon',
    name: 'Srixon',
    rank: 7,
    tier: 'mid',
    specialties: ['Value', 'Distance', 'Tour Quality'],
    popularModels: {
      // Current + Legacy (2010-2024)
      drivers: ['ZX5 Mk II', 'ZX7 Mk II', 'ZX5 LS Mk II', 'ZX5', 'ZX7', 'ZX5 LS', 'Z785', 'Z585', 'Z565', 'Z355', 'Z-Star'],
      woods: ['ZX Fairway', 'ZX5 Fairway', 'ZX7 Fairway', 'Z F85', 'Z F65'],
      hybrids: ['ZX Hybrid', 'ZX5 Hybrid', 'ZX7 Hybrid', 'Z H85', 'Z H65', 'Z H45'],
      irons: ['ZXi', 'ZXi Tour', 'ZXi Max', 'ZX5', 'ZX5 MKII', 'ZX7', 'ZX7 MKII', 'ZX4', 'ZX4 MKII', 'Z-Forged', 'Z-Forged II', 'Z785', 'Z585', 'Z565', 'Z355', 'Z745', 'Z545', 'Z345', 'Z925', 'Z725', 'Z525'],
      wedges: ['Cleveland RTX 6 ZipCore', 'Cleveland RTX 5', 'Cleveland RTX 4', 'Cleveland RTX 3'],
      putters: ['Cleveland Huntington Beach', 'Cleveland Frontline']
    }
  },
  {
    id: 'wilson',
    name: 'Wilson',
    rank: 8,
    tier: 'value',
    specialties: ['Heritage', 'Budget Sets', 'Staff Models'],
    popularModels: {
      // Current + Legacy (2010-2024)
      drivers: ['Dynapower', 'Dynapower Carbon', 'Launch Pad', 'Launch Pad 2022', 'D9', 'D7', 'C300', 'C200', 'FG Tour', 'FG Tour F5'],
      woods: ['Dynapower Fairway', 'Launch Pad Fairway', 'D9 Fairway', 'D7 Fairway'],
      hybrids: ['Launch Pad Hybrid', 'Dynapower Hybrid', 'D9 Hybrid', 'D7 Hybrid'],
      irons: ['Staff Model', 'Staff Model CB', 'Staff Model Blade', 'Dynapower', 'Dynapower Forged', 'Launch Pad', 'D9', 'D9 Forged', 'D7', 'D7 Forged', 'C300', 'C300 Forged', 'C200', 'FG Tour V6', 'FG Tour V4', 'FG Tour 100', 'Ci11', 'Ci9'],
      wedges: ['Staff Model', 'FG Tour', 'Harmonized'],
      putters: ['Staff Model', 'Infinite']
    }
  },
  {
    id: 'pxg',
    name: 'PXG',
    rank: 9,
    tier: 'premium',
    specialties: ['Custom Fitting', 'Luxury', 'Adjustability'],
    popularModels: {
      drivers: ['0311 GEN7', '0311 GEN6', '0311 GEN5', '0311 GEN4', '0311 GEN3', '0311 GEN2', '0311', 'Black Ops', '0811 X', '0811 X+', '0811 XF'],
      woods: ['0341 Fairway', '0341 X Fairway', '0341 GEN5', '0341 GEN4', '0341 GEN3'],
      hybrids: ['0317 Hybrid', '0317 X Hybrid', '0317 GEN5', '0317 GEN4', '0317 GEN3'],
      irons: ['0311 GEN7', '0311 GEN6', '0311 GEN5', '0311 GEN4', '0311 GEN3', '0311 P GEN6', '0311 P GEN5', '0311 P GEN4', '0311 T', '0311 XP', '0311 XF', '0211'],
      wedges: ['Sugar Daddy', 'Sugar Daddy II', '0311', '0311 Forged', '0311 V'],
      putters: ['Battle Ready', 'Battle Ready II', 'Blackjack', 'Brandon', 'Bat Attack', 'Gunboat', 'Mustang']
    }
  },
  {
    id: 'odyssey',
    name: 'Odyssey',
    rank: 10,
    tier: 'premium',
    specialties: ['Putters', 'Alignment', 'Insert Technology'],
    popularModels: {
      putters: ['Ai-One', 'Ai-One Cruiser', 'White Hot OG', 'White Hot OG Seven', 'Tri-Hot 5K', 'Eleven', 'Stroke Lab', 'Stroke Lab Black', 'O-Works', 'O-Works Black', 'Versa', 'Metal-X', 'White Hot Pro', 'White Hot 2.0', 'DFX', 'Tank', '2-Ball']
    }
  },
  {
    id: 'bridgestone',
    name: 'Bridgestone',
    rank: 11,
    tier: 'mid',
    specialties: ['Tour B', 'Low Spin', 'Consistency'],
    popularModels: {
      drivers: ['B3 DD', 'B2', 'B1', 'Tour B X', 'Tour B XD-F', 'Tour B XD-3', 'J15', 'J715', 'J815'],
      woods: ['B3 Fairway', 'B2 Fairway', 'Tour B Fairway', 'J15 Fairway', 'J715 Fairway'],
      hybrids: ['B2 Hybrid', 'Tour B Hybrid', 'J15 Hybrid', 'J715 Hybrid'],
      irons: ['Tour B JGR', 'Tour B JGR HF2', 'Tour B X-CB', 'B3', 'B2', 'B1', 'J15', 'J715', 'J40', 'J40 DPC', 'J40 CB'],
      wedges: ['Tour B', 'Tour B XW', 'J15 Wedge', 'J40 Wedge'],
    }
  },
  {
    id: 'cleveland',
    name: 'Cleveland',
    rank: 12,
    tier: 'mid',
    specialties: ['Wedges', 'RTX Series', 'Spin Control'],
    popularModels: {
      drivers: ['Launcher XL', 'Launcher XL Halo', 'Launcher Halo', 'Launcher HB Turbo', 'Launcher HB', '588', 'Classic XL'],
      woods: ['Launcher XL Halo Fairway', 'Launcher Halo Fairway', 'Launcher HB Fairway'],
      hybrids: ['Launcher XL Halo Hybrid', 'Launcher Halo Hybrid', 'Launcher HB Hybrid'],
      irons: ['Launcher XL', 'Launcher XL Halo', 'Launcher Halo', 'Launcher HB Turbo', 'Launcher HB', '588 MT', '588 TT', 'CG16', 'CG Black', 'CG7'],
      wedges: ['RTX 6 ZipCore', 'RTX Full-Face', 'RTX 5', 'RTX 4', 'RTX-3', 'RTX 2.0', 'RTX', '588 RTX', '588 RTX 2.0', 'CG17', 'CG16', 'CG15', 'CG14', 'CG12', '588', '900 FormForged', 'CBX', 'CBX 2', 'CBX Full-Face', 'Smart Sole'],
      putters: ['Huntington Beach', 'Huntington Beach Soft', 'Frontline', 'TFi 2135', 'Classic Collection']
    }
  },
  {
    id: 'sub70',
    name: 'Sub 70',
    rank: 13,
    tier: 'dtc',
    specialties: ['Direct-to-Consumer', 'Value', 'Performance'],
    popularModels: {
      drivers: ['699 Pro', '699', '639'],
      woods: ['699 Fairway', '639 Fairway'],
      hybrids: ['699 Hybrid', '639 Hybrid', 'TAIII Hybrid'],
      irons: ['799', '799 Pro', '699 Pro', '699', '659 CB', '639 CB', '639 MB', 'TA3', 'TAIII'],
      wedges: ['Sub 70 Wedge', 'Raw Wedge', 'JB Forged Wedge'],
    }
  },
  {
    id: 'takomo',
    name: 'Takomo',
    rank: 14,
    tier: 'dtc',
    specialties: ['Scandinavian Design', 'DTC Value', 'Minimalism'],
    popularModels: {
      drivers: ['101D', '101T', '201D'],
      woods: ['101 Fairway'],
      hybrids: ['101 Hybrid'],
      irons: ['101T', '101', '201', '301', '301 CB', '301 MB'],
      wedges: ['301 Wedge', 'Forged Wedge'],
    }
  },
  {
    id: 'honma',
    name: 'Honma',
    rank: 15,
    tier: 'premium',
    specialties: ['Japanese Craftsmanship', 'Premium', 'Beres Line'],
    popularModels: {
      drivers: ['TR20', 'TR21', 'TW757', 'TW747', 'TW737', 'Beres', 'Beres Black', 'Beres S-08', 'Beres S-07', 'Beres S-06', 'Beres S-05', 'Beres S-03'],
      woods: ['TR20 Fairway', 'TW757 Fairway', 'TW747 Fairway', 'Beres Fairway'],
      hybrids: ['TR20 Hybrid', 'TW757 Hybrid', 'TW747 Hybrid', 'Beres Hybrid'],
      irons: ['TR20', 'TR21', 'TW-X', 'TW-W', 'TW757', 'TW757 Forged', 'TW747', 'TW737', 'Beres', 'Beres S-08', 'Beres S-07', 'Beres S-06', 'Rose Proto'],
      wedges: ['TR20 Wedge', 'TW-W Wedge', 'Beres Wedge'],
      putters: ['TW-PT', 'Beres Putter', 'HP Putter']
    }
  },
  {
    id: 'adams',
    name: 'Adams Golf',
    rank: 16,
    tier: 'value',
    specialties: ['Hybrids', 'Tight Lies', 'Senior Friendly'],
    popularModels: {
      drivers: ['Speedline', 'Speedline Fast 12', 'Speedline Super S', 'Speedline Super LS', 'XTD', 'XTD Ti'],
      woods: ['Tight Lies', 'Tight Lies 2', 'Tight Lies Plus', 'Speedline Fairway', 'Super S Fairway', 'Super LS Fairway'],
      hybrids: ['Tight Lies Hybrid', 'Tight Lies 2 Hybrid', 'Blue', 'Super S Hybrid', 'Super LS Hybrid', 'Pro', 'Pro Hybrid', 'XTD Hybrid', 'Idea', 'Idea Tech', 'CMB', 'A12 OS'],
      irons: ['Blue', 'Idea', 'Idea Tech', 'Idea Super S', 'Idea Super XTD', 'XTD', 'XTD Forged', 'CMB', 'Pro', 'Pro Gold'],
    }
  },
  {
    id: 'newlevel',
    name: 'New Level',
    rank: 17,
    tier: 'dtc',
    specialties: ['Forged Blades', 'Boutique', 'Pure Feel'],
    popularModels: {
      irons: ['MB27', 'MB25', 'CB27', 'CB25', 'P1', 'P1059', '610 Forged', '902 Forged', '1126 Forged'],
      wedges: ['Wedge 27', 'Wedge 25', 'Forged Wedge'],
    }
  },
  {
    id: 'haywood',
    name: 'Haywood',
    rank: 18,
    tier: 'dtc',
    specialties: ['Canadian', 'Eco-Friendly', 'Lifetime Warranty'],
    popularModels: {
      drivers: ['Signature Driver'],
      woods: ['Signature Fairway'],
      hybrids: ['Signature Hybrid'],
      irons: ['Signature', 'Signature Plus', 'Players', 'Players MB'],
      wedges: ['Signature Wedge'],
      putters: ['Signature Putter', 'MB Putter']
    }
  },
  {
    id: 'bettinardi',
    name: 'Bettinardi',
    rank: 19,
    tier: 'premium',
    specialties: ['Milled Putters', 'Tour Proven', 'Precision'],
    popularModels: {
      putters: ['BB Series', 'BB1', 'BB8', 'BB28', 'BB39', 'BB46', 'BB56', 'Studio Stock', 'Studio Stock 3', 'Studio Stock 7', 'Studio Stock 28', 'Studio Stock 38', 'Queen B', 'Queen B 6', 'Queen B 9', 'Queen B 11', 'Queen B 14', 'Inovai', 'Inovai 6.0', 'Inovai 8.0', 'DASS', 'Hex B', 'Kuchar Model 1', 'Kuchar Model 2']
    }
  },
  {
    id: 'scottycameron',
    name: 'Scotty Cameron',
    rank: 20,
    tier: 'premium',
    specialties: ['Iconic Putters', 'Premium', 'Collectible'],
    popularModels: {
      putters: ['Special Select', 'Special Select Newport', 'Special Select Newport 2', 'Special Select Squareback', 'Special Select Fastback', 'Special Select Del Mar', 'Studio Select', 'Phantom', 'Phantom X', 'Phantom X 5', 'Phantom X 5.5', 'Phantom X 7', 'Phantom X 11', 'Phantom X 12', 'Newport', 'Newport 2', 'Newport 2.5', 'Futura', 'Futura X', 'Futura X5', 'Futura X7', 'GoLo', 'GoLo 5', 'GoLo 6', 'Select Newport', 'Select Squareback', 'Teryllium', 'Pro Platinum', 'Studio Stainless', 'Circle T']
    }
  },
];

// Helper function to get brand by ID
export const getBrandById = (id: string): GolfBrand | undefined => {
  return GOLF_BRANDS.find(brand => brand.id === id);
};

// Helper function to get brand by name
export const getBrandByName = (name: string): GolfBrand | undefined => {
  return GOLF_BRANDS.find(brand => 
    brand.name.toLowerCase() === name.toLowerCase()
  );
};

// Get models for a specific brand and club type
export const getModelsForBrand = (
  brandId: string, 
  clubType: 'drivers' | 'woods' | 'hybrids' | 'irons' | 'wedges' | 'putters'
): string[] => {
  const brand = getBrandById(brandId);
  return brand?.popularModels?.[clubType] || [];
};

// Get all brand names (for dropdown)
export const getBrandNames = (): string[] => {
  return GOLF_BRANDS.map(brand => brand.name).sort();
};

// Map club type to model category
export const clubTypeToCategory = (clubType: string): 'drivers' | 'woods' | 'hybrids' | 'irons' | 'wedges' | 'putters' => {
  if (clubType === 'driver') return 'drivers';
  if (clubType.includes('wood')) return 'woods';
  if (clubType.includes('hybrid')) return 'hybrids';
  if (clubType.includes('iron') || ['3-iron', '4-iron', '5-iron', '6-iron', '7-iron', '8-iron', '9-iron'].includes(clubType)) return 'irons';
  if (['pw', 'gw', 'sw', 'lw'].includes(clubType)) return 'wedges';
  if (clubType === 'putter') return 'putters';
  return 'irons'; // default
};
